import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { ChevronLeft } from 'lucide-react';
import { ExportButtons } from '@/components/ExportButtons';
import { ReportTable } from '@/components/ReportTable';
import { apiFetch } from '@/lib/api';
import { formatDate } from '@/lib/utils';

// ─── Types ─────────────────────────────────────────────────────────────────────

interface OutstandingRow {
  id: number;
  number: string;
  customer_name: string;
  date: string;
  total: number;
  amount_paid: number;
  balance: number;
}

interface OutstandingData {
  rows: OutstandingRow[];
  total_balance: number;
}

interface OutstandingResponse {
  data: OutstandingData;
}

// ─── Columns ───────────────────────────────────────────────────────────────────

const COLUMNS = [
  { key: 'number', header: 'Number' },
  { key: 'customer_name', header: 'Customer' },
  { key: 'date', header: 'Date' },
  { key: 'total', header: 'Total', money: true },
  { key: 'amount_paid', header: 'Amount Paid', money: true },
  { key: 'balance', header: 'Balance', money: true },
];

// ─── Page ──────────────────────────────────────────────────────────────────────

export default function OutstandingEstimatesReport() {
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['report', 'outstanding-estimates'],
    queryFn: () => apiFetch<OutstandingResponse>('/api/reports/outstanding-estimates'),
  });

  const report = data?.data;
  const rows = (report?.rows ?? []).map((r) => ({ ...r, date: formatDate(r.date) })) as Record<string, unknown>[];
  const totalsRow = report ? { number: 'Total', balance: report.total_balance } : undefined;

  return (
    <div className="p-6">
      {/* Back link */}
      <Link
        to="/reports"
        className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground mb-3 transition-colors"
      >
        <ChevronLeft className="h-3 w-3" />
        Reports
      </Link>

      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <h1 className="text-2xl font-bold tracking-tight">Outstanding Estimates</h1>
        <ExportButtons endpoint="/api/reports/outstanding-estimates" />
      </div>

      {/* Loading / error */}
      {isLoading && (
        <p className="text-sm text-muted-foreground py-10 text-center">Loading…</p>
      )}
      {isError && (
        <p className="text-sm text-destructive py-10 text-center">
          {error instanceof Error ? error.message : 'Failed to load report'}
        </p>
      )}

      {report && (
        <ReportTable columns={COLUMNS} rows={rows} totalsRow={totalsRow} keyField="id" />
      )}
    </div>
  );
}
